<script lang="ts" setup>
import { ref, watch } from "vue";
import RoleSelector from "./RoleSelector.vue";
import api from "@/services/api";

const props = defineProps<{
  show: boolean;
  orgId?: string; // Optional: if provided, invite to this org (admin feature)
}>();

const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
  (e: "invited"): void;
}>();

const email = ref("");
const selectedRole = ref("OrgMember");
const loading = ref(false);
const error = ref("");

watch(() => props.show, (newValue) => {
  if (!newValue) {
    // Reset form when modal closes
    email.value = "";
    selectedRole.value = "OrgMember";
    error.value = "";
  }
});

async function sendInvite() {
  if (!email.value) {
    error.value = "Please enter an email address";
    return;
  }

  if (!selectedRole.value) {
    error.value = "Please select a role";
    return;
  }

  try {
    loading.value = true;
    error.value = "";

    // First create the invite
    // Note: Don't prefix with /api - the api service already includes it via VITE_API_URL
    const payload: any = {
      email: email.value,
      role: selectedRole.value
    };

    // If orgId is provided (admin feature), include it in the request
    if (props.orgId) {
      payload.orgId = props.orgId;
    }

    const inviteResponse = await api.post("/orgs/invite/create", payload);

    if (inviteResponse.ok) {
      emit("invited");
      emit("update:show", false);
    } else {
      // Try to parse as JSON first (API returns JSON error), fallback to text
      try {
        const errorData = await inviteResponse.json();
        error.value = errorData.message || errorData.title || JSON.stringify(errorData) || "Failed to send invitation";
      } catch {
        const errorText = await inviteResponse.text();
        error.value = errorText || `Failed to send invitation (Status: ${inviteResponse.status})`;
      }
    }
  } catch (err: any) {
    error.value = err.message || "Failed to send invitation";
  } finally {
    loading.value = false;
  }
}

function close() {
  emit("update:show", false);
}
</script>

<template>
  <div
    v-if="show"
    class="modal show d-block"
    tabindex="-1"
    @click.self="close"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">
            <i class="bi bi-person-plus me-2"></i>
            Invite User to Organization
          </h2>
          <button
            type="button"
            class="btn-close"
            @click="close"
            :disabled="loading"
          ></button>
        </div>
        <div class="modal-body">
          <div v-if="error" class="alert alert-danger">
            <i class="bi bi-exclamation-triangle me-2"></i>
            {{ error }}
          </div>

          <div class="mb-3">
            <label for="inviteEmail" class="form-label">Email Address</label>
            <input
              id="inviteEmail"
              v-model="email"
              type="email"
              class="form-control"
              placeholder="user@example.com"
              :disabled="loading"
              @keyup.enter="sendInvite"
            />
            <small class="form-text text-muted">
              The user will receive an invitation email with a link to join your organization
            </small>
          </div>

          <RoleSelector
            v-model="selectedRole"
            :disabled="loading"
          />

          <div class="alert alert-warning mt-3 mb-0">
            <i class="bi bi-info-circle me-2"></i>
            <strong>Note:</strong> The invitation link will expire in 24 hours.
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="bb-btn-cancel"
            @click="close"
            :disabled="loading"
          >
            Cancel
          </button>
          <button
            type="button"
            class="bb-btn-primary-cta"
            @click="sendInvite"
            :disabled="loading || !email"
          >
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="bi bi-send me-2"></i>
            Send Invitation
          </button>
        </div>
      </div>
    </div>
  </div>
<div v-if="show" class="modal-backdrop fade show" @click="close"></div>
</template>

<style scoped lang="scss">
.modal.show {
  display: block;
}
</style>
